import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Callout,
  Checkbox,
  Dialog,
  StatTile,
  Table,
  Td,
  Th,
  Tr,
  useToast,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import type { Tone } from '../lib/format';

/**
 * Bulk import from a spreadsheet.
 *
 * The file is never uploaded blind. Choosing one posts it with `apply: false`,
 * which validates every row through the same services the manual forms call and
 * reports what would happen; only then is there a button that writes. That is
 * the CLI's behaviour too, and it means a wrong file costs nothing.
 *
 * The text is held here between the two requests rather than parked on the
 * server, so there is no import session to expire, resume or clean up.
 */

export type ImportEntity = 'enterprises' | 'accounts' | 'contacts';

type Outcome = 'create' | 'update' | 'unchanged' | 'error';

interface FieldChange {
  field: string;
  from: unknown;
  to: unknown;
}

interface RowResult {
  line: number;
  key: string;
  outcome: Outcome;
  changes?: FieldChange[];
  error?: string;
}

interface FileReport {
  file: string;
  entity: string;
  rows: RowResult[];
  applied: boolean;
  rejected?: string;
}

const OUTCOME_TONE: Record<Outcome, Tone> = {
  create: 'good',
  update: 'info',
  unchanged: 'neutral',
  error: 'critical',
};

const OUTCOME_LABEL: Record<Outcome, string> = {
  create: 'New',
  update: 'Differs',
  unchanged: 'Unchanged',
  error: 'Error',
};

function tally(rows: RowResult[]): Record<Outcome, number> {
  const counts: Record<Outcome, number> = { create: 0, update: 0, unchanged: 0, error: 0 };
  for (const row of rows) counts[row.outcome] += 1;
  return counts;
}

function show(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(empty)';
  return Array.isArray(value) ? value.join('; ') : String(value);
}

export function ImportDialog({
  entity,
  label,
  invalidate,
  onClose,
}: {
  entity: ImportEntity;
  /** How to name the rows in prose: "enterprises", "accounts", "contacts". */
  label: string;
  /** Query keys whose lists this import could change. */
  invalidate: string[][];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [report, setReport] = useState<FileReport | null>(null);
  const [update, setUpdate] = useState(false);

  const send = useMutation({
    mutationFn: (options: { text: string; name: string; apply: boolean; update: boolean }) =>
      api.post<FileReport>('/imports', {
        text: options.text,
        file: options.name,
        apply: options.apply,
        update: options.update,
      }),
  });

  const template = useMutation({
    mutationFn: () => api.download(`/imports/templates/${entity}`, `aura-${entity}-template.csv`),
    onError: (error) =>
      toast(error instanceof ApiError ? error.message : 'Could not download the template.', 'critical'),
  });

  async function choose(chosen: File | undefined): Promise<void> {
    if (!chosen) return;
    const text = await chosen.text();
    setFile({ name: chosen.name, text });
    setReport(null);
    setUpdate(false);
    send.reset();
    const preview = await send.mutateAsync({ text, name: chosen.name, apply: false, update: false });
    setReport(preview);
  }

  async function apply(): Promise<void> {
    if (!file) return;
    const result = await send.mutateAsync({ ...file, apply: true, update });
    setReport(result);

    if (result.rejected || result.rows.some((row) => row.outcome === 'error')) return;

    const counts = tally(result.rows);
    toast(
      `Imported ${label}: ${counts.create} created` +
        (counts.update ? `, ${counts.update} updated` : ''),
    );
    for (const key of invalidate) void queryClient.invalidateQueries({ queryKey: key });
    onClose();
  }

  const counts = report ? tally(report.rows) : null;
  const notable = report?.rows.filter((row) => row.outcome !== 'unchanged') ?? [];
  const writable = counts ? counts.create + (update ? counts.update : 0) : 0;
  const blocked = Boolean(report?.rejected) || (counts?.error ?? 0) > 0;

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={`Import ${label}`}
      description="Choose a CSV to see what it would do. Nothing is written until you confirm."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!report || blocked || writable === 0 || send.isPending}
            onClick={() => void apply()}
          >
            {send.isPending && report ? 'Importing…' : `Import ${writable} row${writable === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(event) => void choose(event.target.files?.[0])}
          />
          <Button variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
            {file ? 'Choose a different file' : 'Choose a CSV file'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={template.isPending}
            onClick={() => template.mutate()}
          >
            Download template
          </Button>
          {file && <span className="text-sm text-ink-2">{file.name}</span>}
        </div>

        {!file && (
          <p className="text-sm text-ink-2">
            The template has the column headings this import accepts. Fill it in, save it as CSV,
            and choose it here.
          </p>
        )}

        {send.isPending && !report && <p className="text-sm text-ink-2">Checking the file…</p>}

        {send.error instanceof ApiError && (
          <Callout tone="critical" title="This file cannot be imported">
            <p className="whitespace-pre-wrap">{send.error.message}</p>
          </Callout>
        )}

        {report?.rejected && (
          <Callout tone="critical" title="Nothing was written">
            {report.rejected}
          </Callout>
        )}

        {counts && !report?.rejected && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="New" value={counts.create} tone={counts.create ? 'good' : 'neutral'} />
            <StatTile label="Differ" value={counts.update} tone={counts.update ? 'info' : 'neutral'} />
            <StatTile label="Unchanged" value={counts.unchanged} />
            <StatTile
              label="Errors"
              value={counts.error}
              tone={counts.error ? 'critical' : 'neutral'}
            />
          </div>
        )}

        {counts !== null && counts.error > 0 && (
          <Callout tone="critical" title={`${counts.error} row(s) could not be imported`}>
            Fix them in the spreadsheet and choose the file again. Nothing is written while any row
            is in error.
          </Callout>
        )}

        {counts !== null && counts.update > 0 && !report?.applied && (
          <Checkbox
            label="Also update rows that already exist"
            hint="Rows that differ are left exactly as they are unless this is ticked."
            checked={update}
            onChange={(event) => setUpdate(event.target.checked)}
          />
        )}

        {notable.length > 0 && (
          <div className="max-h-72 overflow-y-auto rounded-md border border-border">
            <Table>
              <thead>
                <tr>
                  <Th align="right">Line</Th>
                  <Th>Row</Th>
                  <Th>Outcome</Th>
                  <Th>Detail</Th>
                </tr>
              </thead>
              <tbody>
                {notable.map((row) => (
                  <Tr key={`${row.line}-${row.key}`}>
                    <Td align="right" className="tabular-nums text-ink-2">
                      {row.line}
                    </Td>
                    <Td className="font-medium">{row.key}</Td>
                    <Td>
                      <Badge tone={OUTCOME_TONE[row.outcome]}>{OUTCOME_LABEL[row.outcome]}</Badge>
                    </Td>
                    <Td className="text-ink-2">
                      {row.error ??
                        row.changes
                          ?.map((c) => `${c.field}: ${show(c.from)} → ${show(c.to)}`)
                          .join(', ')}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {counts !== null && !blocked && (
          <p className="text-xs text-ink-2">
            Imported rows go through the same checks and the same audit trail as rows typed into the
            form, and are recorded against this file and line number. Rows are written one at a
            time, so if something fails midway the earlier rows stay — running the file again
            reports them as unchanged.
          </p>
        )}
      </div>
    </Dialog>
  );
}
